import { useAuthenticationContext } from '@/Contexts/App/AuthenticationContext';
import AuthenticatedContextProvider from '@/Contexts/Authenticated';
import FilingCabinetPage from '@/Helpers/FilingCabinetPage';
import { type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import CheckDetail from '@/Pages/CheckDetail/CheckDetail';
import CheckOverview from '@/Pages/CheckOverview/CheckOverview';
import CurrentLocks from '@/Pages/CurrentLocks/CurrentLocks';
import EFactTool from '@/Pages/EFactTool/EFactTool';
import NewWordsPage from '@/Pages/NewWords/NewWordsPage';
import TextBlockDetail from '@/Pages/TextBlockDetail/TextBlockDetail';
import TextBlockOverview from '@/Pages/TextBlockOverview/TextBlockOverview';
import UserDefaults from '@/Pages/UserDefaults/UserDefaults';
import AuthorizedRoute from '@/Routes/Authenticated/AuthorizedRoute';
import Grid from '@mui/material/Grid';
import { useState } from 'react';
import { Navigate, Outlet, Route, useLocation } from 'react-router';
import Header from '../../Components/Header/Header';
import Menu from '../../Components/Menu/Menu';
import CreatePrescriptions from '../../Components/Prescriptions/CreatePrescriptions';
import GeneralSearch from '../../Pages/GeneralSearch/GeneralSearch';
import WorklistPage from '../../Pages/GenericWorklist/GenericWorklist';
import ROW2Routes from './ROW2/ROW2Routes';
import RazorRoutes from './Razor/RazorRoutes';
import { RouteKeyGenerator } from './RouteKeyGenerator';
import NomenclaturesOverview from '@/Pages/NomenclaturesOverview/NomenclaturesOverview';
import PermissionsConfiguration from '@/Pages/PermissionsConfiguration/PermissionsConfiguration';

const AuthRoot = (): React.ReactElement => {
    const location = useLocation();
    const authenticationContext = useAuthenticationContext();
    const [isMenuVisible, setIsMenuVisible] = useState(true);

    if (!authenticationContext.isLoggedIn()) {
        const redirectTo = location.pathname;
        return (<Navigate to={`/login?${new URLSearchParams({ redirectTo })}`} />);
    }

    const onClickLogo = (): void => {
        setIsMenuVisible(!isMenuVisible);
    };

    return (
        <AuthenticatedContextProvider>
            <Header onClickLogo={onClickLogo} />
            <Grid container>
                <Menu isVisible={isMenuVisible} />
                <Grid size="grow" sx={{ position: 'relative', height: 'calc(100vh - 30px)' }}>
                    <Outlet />
                </Grid>
            </Grid>
        </AuthenticatedContextProvider>
    );
};

const AuthenticatedRoutes = (): React.ReactElement => {
    return (
        <Route element={<AuthRoot />}>
            <Route index={true} element={<Navigate to="/dashboard" replace />} />
            {RazorRoutes()}
            {ROW2Routes()}
            {/* AuthorizedRoute paths are based on tblView due to how UserLogic.CanOpenPage in ROW1 works */}
            <Route path="EFactTool" element={<EFactTool />} />
            <Route
                path="GeneralSearch/:term"
                element={(
                    <AuthorizedRoute path="/Dashboard/GeneralSearch">
                        <GeneralSearchPage />
                    </AuthorizedRoute>
                )}
            />
            <Route
                path="Worklist"
                element={(
                    <AuthorizedRoute path="/Worklist">
                        <FilingCabinetPage renderPage={GenericWorklistComponent} reuseFile={true} />
                    </AuthorizedRoute>
                )}
                key={RouteKeyGenerator.key}
            />
            <Route path="PrescriptionsAdd" element={<CreatePrescriptions />} />
            <Route
                path="TextBlocks"
                element={(
                    <AuthorizedRoute path="/System/TextBlocks">
                        <TextBlocks />
                    </AuthorizedRoute>
                )}
                key={RouteKeyGenerator.key}
            />
            <Route
                path="System/AddTextBlock"
                element={(
                    <AuthorizedRoute path="/System/AddTextBlock">
                        <AddTextBlock />
                    </AuthorizedRoute>
                )}
                key={RouteKeyGenerator.key}
            />
            <Route
                path="System/DetailTextBlock/:id"
                element={(
                    <AuthorizedRoute path="/System/DetailTextBlock">
                        <DetailTextBlock />
                    </AuthorizedRoute>
                )}
                key={RouteKeyGenerator.key}
            />
            <Route
                path="currentLocks"
                element={(
                    <AuthorizedRoute path="/System/Locks">
                        <CurrentLocksPage />
                    </AuthorizedRoute>
                )}
                key={RouteKeyGenerator.key}
            />
            <Route path="NewWords" element={<NewWordsOverView />} key={RouteKeyGenerator.key} />
            <Route
                path="Checks"
                element={(
                    <AuthorizedRoute path="/System/Checks">
                        <ChecksPage />
                    </AuthorizedRoute>
                )}
                key={RouteKeyGenerator.key}
            />
            <Route
                path="DetailCheck/:id?"
                element={(
                    <AuthorizedRoute path="/System/Checks">
                        <AddCheckPage />
                    </AuthorizedRoute>
                )}
                key={RouteKeyGenerator.key}
            />
            <Route
                path="UserDefaults/:id?"
                element={(
                    <AuthorizedRoute path="/System/UserDefaults">
                        <UserDefaultsPage />
                    </AuthorizedRoute>
                )}
                key={RouteKeyGenerator.key}
            />
            <Route
                path="NomenclatureOverview"
                element={(
                    <AuthorizedRoute path="/Nomenclature/NomenclatureManagement">
                        <NomenclaturesOverviewPage />
                    </AuthorizedRoute>
                )}
            />
            <Route
                path="UserManagement"
                element={(
                    <AuthorizedRoute path="/UserManagement">
                        <PermissionsConfigurationPage />
                    </AuthorizedRoute>
                )}
            />
        </Route>
    );
};

const GenericWorklistComponent = (props: { state: FilingCabinetItem }): React.ReactElement => {
    return <WorklistPage initialState={props.state} />;
};

const TextBlocks = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <TextBlockOverview initialState={props.state} />} />;
};

const AddTextBlock = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <TextBlockDetail initialState={props.state} isEdit={false} />} />;
};

const DetailTextBlock = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <TextBlockDetail initialState={props.state} isEdit={true} />} />;
};

const CurrentLocksPage = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <CurrentLocks initialState={props.state} />} />;
};

const NewWordsOverView = (): React.ReactElement => {
    return <FilingCabinetPage reuseFile={true} renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <NewWordsPage initialState={props.state} />} />;
};

const ChecksPage = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <CheckOverview initialState={props.state} />} />;
};

const AddCheckPage = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <CheckDetail initialState={props.state} />} />;
};

const UserDefaultsPage = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <UserDefaults initialState={props.state} />} />;
};

const GeneralSearchPage = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={(props: { state: FilingCabinetItem }): React.ReactElement => <GeneralSearch initialState={props.state} />} />;
};

const NomenclaturesOverviewPage = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={({ state }: { state: FilingCabinetItem }): React.ReactElement => <NomenclaturesOverview initialState={state} />} />;
};

const PermissionsConfigurationPage = (): React.ReactElement => {
    return <FilingCabinetPage renderPage={({ state }: { state: FilingCabinetItem }): React.ReactElement => <PermissionsConfiguration initialState={state} />} />;
};

export default AuthenticatedRoutes;
